import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadPhotoProof, resolveErrorMessage } from '@kidshome/api-contract';
import { PHOTO_PROOF_MAX_BYTES_DEFAULT } from '@kidshome/shared';
import { Button } from '@kidshome/ui';
import { tDynamic } from '@/shared/lib/i18n';
import {
  processPhotoProofFile,
  PhotoProofImageProcessingError,
  type PhotoProofImageError,
} from '@/features/upload-photo-proof/photo-proof-image';

/** Max upload size (bytes). Mirrors the backend `PHOTO_PROOF_MAX_BYTES` ceiling so the client rejects
 * an oversized photo with a kind message before wasting an upload that the server would 413. */
const MAX_BYTES = PHOTO_PROOF_MAX_BYTES_DEFAULT;

/** Maps a client-side processing failure to a kind, translatable string (mirrors the sibling
 * avatar-photo/reward-photo error-key maps). */
const PROCESSING_ERROR_KEY: Record<PhotoProofImageError, string> = {
  'decode-failed': 'photoProof.decodeFailed',
  'encode-failed': 'photoProof.encodeFailed',
};

type Stage = { kind: 'idle' } | { kind: 'preview'; blob: Blob; previewUrl: string };

/**
 * Kid photo-proof capture (chore detail). A real, labelled button triggers a hidden
 * `<input type="file" accept="image/*" capture="environment">` so a phone offers the camera. After a
 * pick, the file is processed ENTIRELY client-side (downscale + WebP re-encode via `photo-proof-image`,
 * mirroring `upload-avatar-photo`/`upload-reward-photo`) — this strips EXIF/GPS/timestamp metadata a
 * phone camera embeds (e.g. precise location, when Camera location-tagging is on) BEFORE the file is
 * ever staged or uploaded, so the RAW bytes never reach the preview or the server. We then show a
 * PREVIEW of the processed image with confirm/retake; on confirm we upload the processed blob
 * (multipart) and hand the resulting `photoProofKey` to `onUploaded`, which the page passes to
 * `complete()` (a photo-proof chore then lands in `pending_approval`).
 *
 * Validation is client-side and kind: non-images, oversized files, and processing failures are all
 * rejected with a friendly, non-shaming message (also a `role=alert` for AT); an upload failure lets the
 * kid simply try again. The object URL for the preview is revoked on change/unmount so no blob URL leaks.
 */
export function PhotoCapture({
  occurrenceId,
  onUploaded,
}: {
  occurrenceId: string;
  /** Called with the opaque `photoProofKey` once the upload succeeds. */
  onUploaded: (photoProofKey: string) => void;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  // A local validation message (wrong type / too large / processing failure), separate from the upload error.
  const [validationError, setValidationError] = useState<string | null>(null);
  // True while the picked file is being decoded/downscaled/re-encoded client-side (between pick and
  // preview) — mirrors the sibling avatar-photo/reward-photo `processing` guard.
  const [processing, setProcessing] = useState(false);

  const upload = useUploadPhotoProof();

  // Revoke the preview object URL when it changes or the component unmounts (no blob leak).
  useEffect(() => {
    const url = stage.kind === 'preview' ? stage.previewUrl : null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [stage]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValidationError(null);
    upload.reset();
    const file = e.target.files?.[0];
    // Reset the input value so picking the SAME file again still fires `change` (retake works).
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setValidationError(t('photoProof.wrongType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setValidationError(t('photoProof.tooLarge'));
      return;
    }
    setProcessing(true);
    try {
      const blob = await processPhotoProofFile(file);
      setStage({ kind: 'preview', blob, previewUrl: URL.createObjectURL(blob) });
    } catch (err) {
      const reason = err instanceof PhotoProofImageProcessingError ? err.reason : 'decode-failed';
      setValidationError(tDynamic(t, PROCESSING_ERROR_KEY[reason]));
    } finally {
      setProcessing(false);
    }
  };

  const onConfirm = (): void => {
    if (stage.kind !== 'preview') return;
    // The processed blob is a WebP; name it so the multipart part is well-formed.
    const file = new File([stage.blob], 'photo-proof.webp', { type: 'image/webp' });
    upload.mutate(
      { occurrenceId, file },
      {
        onSuccess: ({ photoProofKey }) => {
          setStage({ kind: 'idle' });
          onUploaded(photoProofKey);
        },
      },
    );
  };

  const onRetake = (): void => {
    upload.reset();
    setStage({ kind: 'idle' });
    inputRef.current?.click();
  };

  const uploadErrorMessage =
    upload.error != null
      ? (() => {
          const m = resolveErrorMessage(upload.error);
          return tDynamic(t, m.key, m.params);
        })()
      : null;

  return (
    <div className="flex flex-col gap-3">
      {/* The hidden input is driven by a real labelled button so it stays keyboard/AT reachable.
          Disabled while a pick is processing or an upload is in flight — same guard as Confirm/Retake
          below — so a second pick reachable directly via keyboard/AT can't start a race with the one
          already under way (see the sibling guard on the avatar-photo hidden inputs). */}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        disabled={processing || upload.isPending}
        onChange={(e) => void onPick(e)}
      />

      {stage.kind === 'idle' ? (
        <Button
          variant="outline"
          size="sm"
          disabled={processing}
          onClick={() => inputRef.current?.click()}
        >
          {processing ? t('photoProof.processing') : t('photoProof.addPhoto')}
        </Button>
      ) : (
        <div className="flex flex-col gap-3">
          <img
            src={stage.previewUrl}
            alt={t('photoProof.previewAlt')}
            className="max-h-64 w-full rounded-card object-contain bg-surface-muted"
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              disabled={upload.isPending}
              onClick={onConfirm}
            >
              {upload.isPending ? t('photoProof.uploading') : t('photoProof.usePhoto')}
            </Button>
            <Button variant="ghost" size="sm" disabled={upload.isPending} onClick={onRetake}>
              {t('photoProof.retake')}
            </Button>
          </div>
        </div>
      )}

      {validationError && (
        <p role="alert" className="text-sm font-semibold text-danger">
          {validationError}
        </p>
      )}
      {/* The SPECIFIC translated reason (stable server code via resolveErrorMessage) — a kid whose
          photo was rejected for a concrete reason (e.g. unsupported type) can act on it; retrying
          the same file against a generic message could never succeed. */}
      {uploadErrorMessage && (
        <p role="alert" className="text-sm font-semibold text-danger">
          {uploadErrorMessage}
        </p>
      )}
    </div>
  );
}
